import React, { useState, useEffect } from 'react';
import HeroSection from '../components/HeroSection';
import ProductGrid from '../components/ProductGrid';
import { ArrowRight, Award, Heart, Users, Sparkles, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Home = () => {
  const [featuredStatues, setFeaturedStatues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const apiUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Lifestyle gallery teaser images
  const lifestyleImages = [
    {
      url: '/living_room_gallery_corrected_table.png',
      title: 'Living Room Gallery with Corrected Coffee Table Display'
    },
    {
      url: '/luxury_wooden_chalet.png',
      title: 'Luxury Wooden Chalet with Original Elements'
    },
    {
      url: '/pool_area_impressions.png',
      title: 'Luxury Pool Area with Artourius Sculptures'
    }
  ];

  // Fetch featured statues from database
  useEffect(() => {
    const fetchFeaturedStatues = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/products/statues`);
        if (response.ok) {
          const data = await response.json();
          // Get first 6 statues for featured section
          const featured = data.slice(0, 6).map(statue => ({
            id: statue.id,
            title: statue.name,
            category: 'sculpture',
            description: statue.description,
            image: statue.images && statue.images.length > 0 ? statue.images[0] : '/placeholder-statue.png',
            price: parseFloat(statue.price.replace('CHF', '').trim()) || 0,
            material: statue.material || 'Hand-crafted',
            dimensions: `${statue.height} × ${statue.width} × ${statue.depth}`,
            tags: ['sculpture', 'luxury', 'handcrafted']
          }));
          setFeaturedStatues(featured);
        }
      } catch (error) {
        console.error('Failed to fetch featured statues:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedStatues();
  }, [apiUrl]);

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <HeroSection />

      {/* Lifestyle Gallery */}
      <section className="py-24 px-6 bg-black">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <p className="text-[hsl(45,100%,70%)] font-semibold text-sm uppercase tracking-wider mb-4">
              Interior Inspirations
            </p>
            <Link to="/ideas-impressions" className="inline-block group">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 transition-all duration-300 group-hover:scale-105">
                <span className="text-[hsl(45,100%,70%)] group-hover:text-[hsl(45,100%,75%)]">Lifestyle</span> Gallery
              </h2>
            </Link>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto">
              Experience our collection beautifully styled in real living spaces
            </p>
          </div>

          {/* Image Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {lifestyleImages.map((image, index) => (
              <div
                key={index}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedImageIndex(index);
                }}
                className="group relative aspect-square overflow-hidden rounded-lg bg-gray-900 cursor-pointer"
              >
                <img
                  src={image.url}
                  alt={image.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                
                {/* Hover overlay with title */}
                <div className="absolute inset-0 p-6 flex items-end pointer-events-none">
                  <div className="transform transition-all duration-500 opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0">
                    <h3 className="text-white text-sm font-semibold">
                      {image.title}
                    </h3>
                  </div>
                </div>
                
                {/* Golden border on hover */}
                <div className="absolute inset-0 border-2 border-transparent group-hover:border-[hsl(45,100%,70%)] transition-all duration-500 rounded-lg pointer-events-none" />
              </div>
            ))}
          </div>
          
          {/* See more link */}
          <div className="text-center mt-10">
            <Link 
              to="/living-with-artourius" 
              className="inline-flex items-center gap-2 text-[hsl(45,100%,70%)] font-semibold text-lg hover:text-[hsl(45,100%,80%)] transition-colors group"
            >
              See more
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Collection */}
      {loading ? (
        <div className="py-24 px-6 bg-black text-center">
          <div className="text-[hsl(45,100%,70%)] text-2xl">Loading featured collection...</div>
        </div>
      ) : (
        <ProductGrid
          products={featuredStatues}
          title="Discover our luxury handcrafted Sculptures"
          subtitle="Featured Works"
        />
      )}

      {/* CTA Section */}
      <section className="py-32 px-6 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[hsl(45,100%,70%)] rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[hsl(45,100%,70%)] rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight">
            Let's create
            <br />
            <span className="text-[hsl(45,100%,70%)]">something unique</span>
            <br />
            together
          </h2>
          
          <p className="text-gray-300 text-lg md:text-xl mb-12 max-w-2xl mx-auto">
            Each piece is a testament to craftsmanship and artistic vision. Explore our collections or reach out for custom commissions.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-20">
            <Link
              to="/contact"
              className="group px-8 py-4 bg-[hsl(45,100%,70%)] text-black font-bold rounded-full hover:shadow-[0_0_30px_rgba(255,215,0,0.5)] transition-all duration-300 hover:scale-105 flex items-center gap-2"
            >
              Get in touch
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            
            <Link
              to="/sculpture"
              className="group px-8 py-4 border-2 border-[hsl(45,100%,70%)] text-[hsl(45,100%,70%)] font-bold rounded-full hover:bg-[hsl(45,100%,70%)] hover:text-black transition-all duration-300 hover:scale-105"
            >
              Explore collections
            </Link>
          </div>

          {/* About Us Section */}
          <div className="max-w-6xl mx-auto mt-20">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Where art meets
                <br />
                <span className="text-[hsl(45,100%,70%)]">extraordinary craftsmanship</span>
              </h2>
              <p className="text-gray-300 text-lg max-w-3xl mx-auto leading-relaxed">
                Artourius Design was born from a passion for creating unique, handcrafted sculptures that transcend ordinary decoration. Each piece tells a story of dedication, skill, and artistic vision.
              </p>
            </div>

            {/* Story and Philosophy */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
              <div>
                <h3 className="text-2xl font-bold text-white mb-4">Our Story</h3>
                <div className="space-y-4 text-gray-300 leading-relaxed">
                  <p>
                    Founded in the heart of Switzerland, Artourius Design represents the pinnacle of luxury sculptural art. Our journey began with a simple belief: that everyday objects can be transformed into extraordinary works of art.
                  </p>
                  <p>
                    Each piece in our collection is carefully conceived and meticulously crafted by master artisans who have dedicated their lives to perfecting their craft. We combine traditional techniques with contemporary design sensibilities to create sculptures that are both timeless and relevant.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-white mb-4">Our Philosophy</h3>
                <div className="space-y-4 text-gray-300 leading-relaxed">
                  <p>
                    We believe that true luxury lies not in mass production, but in the unique character of handcrafted pieces. Each sculpture in our collection is created in strictly limited editions, ensuring that our clients own something truly exclusive.
                  </p>
                  <p>
                    Beyond creating beautiful objects, we strive to forge lasting relationships with our clients. Whether you're selecting from our existing collections or commissioning a custom piece, we're dedicated to bringing your vision to life with uncompromising quality and service.
                  </p>
                </div>
              </div>
            </div>

            {/* Our Values */}
            <div className="mt-16">
              <h3 className="text-2xl font-bold text-white text-center mb-10">Our Values</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Craftsmanship */}
                <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800 hover:border-[hsl(45,100%,70%)] transition-all duration-300 group">
                  <div className="w-14 h-14 rounded-full bg-[hsl(45,100%,70%)]/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Heart className="w-7 h-7 text-[hsl(45,100%,70%)]" />
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">Craftsmanship</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">Every piece is handcrafted with meticulous attention to detail and passion for perfection.</p>
                </div>

                {/* Luxury */}
                <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800 hover:border-[hsl(45,100%,70%)] transition-all duration-300 group">
                  <div className="w-14 h-14 rounded-full bg-[hsl(45,100%,70%)]/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Sparkles className="w-7 h-7 text-[hsl(45,100%,70%)]" />
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">Luxury</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">We use only the finest materials to create timeless pieces that exude elegance and sophistication.</p>
                </div>

                {/* Excellence */}
                <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800 hover:border-[hsl(45,100%,70%)] transition-all duration-300 group">
                  <div className="w-14 h-14 rounded-full bg-[hsl(45,100%,70%)]/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Award className="w-7 h-7 text-[hsl(45,100%,70%)]" />
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">Excellence</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">Our commitment to quality ensures each creation becomes a cherished heirloom.</p>
                </div>

                {/* Personalization */}
                <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800 hover:border-[hsl(45,100%,70%)] transition-all duration-300 group">
                  <div className="w-14 h-14 rounded-full bg-[hsl(45,100%,70%)]/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Users className="w-7 h-7 text-[hsl(45,100%,70%)]" />
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">Personalization</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">We work closely with clients to bring their unique visions to life through custom commissions.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-black border-t border-gray-800">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-4">
              <img
                src="/artourius-logo.png"
                alt="Artourius Design"
                className="h-10 w-auto"
              />
            </div>
            
            <div className="text-gray-400 text-sm text-center md:text-left">
              <p>© 2025 Artourius Design. All rights reserved.</p>
              <p className="mt-1">Handcrafted with precision and passion.</p>
            </div>
            
            <div className="flex gap-6">
              <Link to="/about" className="text-gray-400 hover:text-[hsl(45,100%,70%)] transition-colors">
                About
              </Link>
              <Link to="/contact" className="text-gray-400 hover:text-[hsl(45,100%,70%)] transition-colors">
                Contact
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Lightbox Modal for Lifestyle Gallery */}
      {selectedImageIndex !== null && (
        <div
          className="fixed inset-0 bg-black/95 backdrop-blur-xl flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
          onClick={() => setSelectedImageIndex(null)}
        >
          {/* Close Button */}
          <button
            onClick={(e) => { e.stopPropagation(); setSelectedImageIndex(null); }}
            className="absolute top-8 right-8 w-12 h-12 rounded-full bg-[hsl(45,100%,70%)] flex items-center justify-center hover:scale-110 transition-transform"
            style={{ zIndex: 10000 }}
          >
            <X className="w-6 h-6 text-black" />
          </button>

          {/* Previous Arrow */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedImageIndex((prev) => 
                prev === 0 ? lifestyleImages.length - 1 : prev - 1
              );
            }}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 border-2 border-[hsl(45,100%,70%)] flex items-center justify-center hover:bg-[hsl(45,100%,70%)] hover:scale-110 transition-all group"
            style={{ zIndex: 10000 }}
          >
            <ChevronLeft className="w-6 h-6 text-[hsl(45,100%,70%)] group-hover:text-black" />
          </button>

          {/* Next Arrow */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedImageIndex((prev) => 
                prev === lifestyleImages.length - 1 ? 0 : prev + 1
              );
            }}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 border-2 border-[hsl(45,100%,70%)] flex items-center justify-center hover:bg-[hsl(45,100%,70%)] hover:scale-110 transition-all group"
            style={{ zIndex: 10000 }}
          >
            <ChevronRight className="w-6 h-6 text-[hsl(45,100%,70%)] group-hover:text-black" />
          </button>

          {/* Image */}
          <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={lifestyleImages[selectedImageIndex].url}
              alt={lifestyleImages[selectedImageIndex].title}
              className="w-full h-auto max-h-[85vh] object-contain rounded-2xl"
            />
            <div className="text-center mt-6">
              <h3 className="text-xl font-semibold text-white">
                {lifestyleImages[selectedImageIndex].title}
              </h3>
              <p className="text-sm text-gray-400 mt-2">
                {selectedImageIndex + 1} / {lifestyleImages.length}
              </p>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Home;